import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { cartStorage, cartSync } from '../utils/cartUtils';
import { database } from '../supabase';

export interface CartItem {
  id: string;
  name: string;
  price: string;
  image: string;
  quantity: number;
  stock?: number | undefined;
  cartItemId?: string;
}

export const useCart = () => {
  const { isAuthenticated, user } = useAuth();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize cart on mount and when auth state changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const initializeCart = async () => {
      setIsLoading(true);
      try {
        const cart = await cartSync.loadCart(isAuthenticated, (user as any)?.id, database);
        setCartItems(cart);
      } catch (error) {
        console.error('Failed to load cart:', error);
        // Fallback to empty cart
        setCartItems([]);
      } finally {
        setIsLoading(false);
      }
    };

    initializeCart();
  }, [isAuthenticated, (user as any)?.id]);

  const addToCart = async (item: Omit<CartItem, 'quantity'>) => {
    await cartSync.handleCartOperation('add', cartItems, setCartItems, isAuthenticated, (user as any)?.id, database, item);
  };

  const removeFromCart = async (id: string) => {
    await cartSync.handleCartOperation('remove', cartItems, setCartItems, isAuthenticated, (user as any)?.id, database, id);
  };

  const updateCartQuantity = async (id: string, quantity: number) => {
    await cartSync.handleCartOperation('update', cartItems, setCartItems, isAuthenticated, (user as any)?.id, database, id, quantity);
  };

  const clearCart = () => {
    setCartItems([]);
    cartStorage.clear();
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => {
      const price = parseFloat(item.price.replace('$', ''));
      return total + (price * item.quantity);
    }, 0);
  };

  const getCartItemCount = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  return {
    cartItems,
    isLoading,
    addToCart,
    removeFromCart,
    updateCartQuantity,
    clearCart,
    getCartTotal,
    getCartItemCount
  };
};
